import React, { ChangeEventHandler, useContext, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import Alert from '../components/Alert'
import Page from '../components/Page'
import useAuth from '../hooks/useAuth'
import Form from '../components/Form'
import { isValidationFailedResponse } from '../types'
import { PageURLs } from '../common'
import { useDispatch, useSelector } from 'react-redux'
import { logError, login } from '../store/slices/app'
import { RootState } from '../store'
import { ThemeContext } from '../theme-context'
import 'react-phone-number-input/style.css'
import PhoneInput, { Value as E164Number } from 'react-phone-number-input'

const Register = () => {
  const api = useAuth()
  const { transferBookingIds } = useSelector((state: RootState) => state.app)

  const formEl = useRef<HTMLFormElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const history = useHistory()
  const { reCAPTCHA } = useContext(ThemeContext)

  const [errorMessage, setErrorMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const [step, setStep] = useState<'first' | 'second'>('first')
  const initialFormState = {
    email: '',
    title: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    password: '',
  }
  const [form, setForm] = useState({ ...initialFormState })

  const handleOnInput = (key: string): ChangeEventHandler<HTMLInputElement> => {
    return (event) => {
      setForm({ ...form, [key]: event.target.value })
    }
  }

  const handlePhoneNumberInput = (number?: E164Number) => {
    setForm({ ...form, phone_number: number || '' })
  }

  const goToStepOne = () => {
    setForm({ ...initialFormState })
    setErrorMessage('')
    setValidationErrors({})
  }

  const handleUserEmail: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()

    if (isLoading) return

    setIsLoading(true)
    setErrorMessage('')
    setValidationErrors({})

    try {
      await api.validateEmail(form.email)

      setStep('second')
    } catch (error) {
      dispatch(logError(error))
      console.log(error)
      if (isValidationFailedResponse(error)) {
        setErrorMessage('There are a few errors')
        setValidationErrors(error.response.data.data.params)
      } else {
        setErrorMessage('Failed to validate email')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleRegister: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()

    if (!grecaptcha) {
      setErrorMessage('reCAPTCHA not loaded')
      return
    }

    if (isLoading) return

    setIsLoading(true)
    setErrorMessage('')
    setValidationErrors({})

    if (!(event.target as HTMLFormElement).checkValidity()) {
      setErrorMessage('Please fill all required fields')
      setIsLoading(false)

      return
    }

    try {
      const recaptchaResponse = await grecaptcha.execute(reCAPTCHA, { action: 'register' })
      const response = await api.register({ ...form, recaptcha: recaptchaResponse })
      dispatch(login(response.data))
      history.push(transferBookingIds.length > 0 ? PageURLs.CompleteBooking : PageURLs.Home)
    } catch (error) {
      dispatch(logError(error))
      if (isValidationFailedResponse(error)) {
        setErrorMessage('There are a few errors')
        setValidationErrors(error.response.data.data.params)
      } else {
        setErrorMessage('Failed to register. Try again later.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (step === 'first') {
    return (
      <Page className="bg-pink">
        <div className="login-wrap min-vh-100 d-flex align-items-center flex-column justify-content-center flex-wrap">
          <div className="container">
            <h1 className="text-center">Get Started</h1>

            <div className="row justify-content-center align-items-center">
              <div className="col-12 col-lg-5 col-xxl-4 order-2 order-lg-0">
                <form ref={formEl} className="my-3 my-lg-6" onSubmit={handleUserEmail} noValidate>
                  {!!errorMessage && <Alert type="danger" msg={errorMessage} />}
                  <div className="form-group">
                    <label htmlFor="user_email" className="sr-only">
                      Your Email
                    </label>
                    <input
                      type="email"
                      autoComplete="email"
                      className="form-control form-control-lg"
                      id="user_email"
                      name="user_email"
                      placeholder="Your Email"
                      onChange={handleOnInput('email')}
                      value={form.email}
                      required
                    />
                    <Form.ValidationError message={validationErrors['email']} />
                  </div>
                  <button type="submit" className="btn btn-secondary btn-block border-0 btn-lg">
                    Sign up with email
                  </button>
                </form>
              </div>
              {/*<div className="col-12 col-lg-2 col-xxl-1 order-1 mt-4 mt-lg-0">*/}
              {/*  <span className="h6 d-block text-center">OR</span>*/}
              {/*</div>*/}
              {/*<div className="col-12 col-lg-5 col-xxl-4 order-0 order-lg-2 mt-4 mt-lg-0">*/}
              {/*  <SocialLogins></SocialLogins>*/}
              {/*</div>*/}
            </div>
          </div>
          <div className="register-link">
            <div className="container">
              <div className="row">
                <div className="col-12 text-center">
                  <p>
                    Already have account?{' '}
                    <Link to="/login/" className="text-secondary">
                      Sign in
                    </Link>
                  </p>
                  <p>
                    Forgotten your password?{' '}
                    <Link to={PageURLs.PasswordEmail} className="reset-password-link text-secondary">
                      Reset Password
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    )
  }

  return (
    <Page className="bg-pink">
      <div className="login-wrap min-vh-100 d-flex align-items-center flex-column justify-content-center flex-wrap">
        <div className="container">
          <div className="row">
            <div className="col">
              <button className="btn btn-link text-secondary" onClick={goToStepOne}>
                <span className="icon-arrow-right direction-left d-inline-block"></span>
              </button>
            </div>
          </div>

          <div className="row justify-content-center align-items-center">
            <div className="col-12 col-lg-5 col-xxl-4">
              <form ref={formEl} className="my-3 my-lg-6" id="register-step-two" onSubmit={handleRegister} noValidate>
                {!!errorMessage && <Alert type="danger" msg={errorMessage} />}
                <div className="form-group mb-4">
                  <label htmlFor="user_first_name" className="h5">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    autoComplete="given-name"
                    id="user_first_name"
                    name="user_first_name"
                    placeholder="Your First Name"
                    onChange={handleOnInput('first_name')}
                    value={form.first_name}
                    required
                  />
                  <Form.ValidationError message={validationErrors['first_name']} />
                </div>
                <div className="form-group mb-4">
                  <label htmlFor="user_last_name" className="h5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    autoComplete="family-name"
                    id="user_last_name"
                    name="user_last_name"
                    placeholder="Your Last Name"
                    onChange={handleOnInput('last_name')}
                    value={form.last_name}
                    required
                  />
                  <Form.ValidationError message={validationErrors['last_name']} />
                </div>
                <div className="form-group mb-4">
                  <label htmlFor="phone" className="h5">
                    Mobile
                  </label>
                  <PhoneInput
                    id="phone"
                    name="phone"
                    placeholder="Phone number"
                    onChange={handlePhoneNumberInput}
                    value={form.phone_number}
                    required
                  />

                  <Form.ValidationError message={validationErrors['phone_number']} />
                </div>
                <div className="form-group mb-4">
                  <label htmlFor="phone" className="h5">
                    Password
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="form-control form-control-lg"
                    id="password"
                    name="password"
                    placeholder="Password"
                    onChange={handleOnInput('password')}
                    value={form.password}
                    required
                  />
                  <Form.ValidationError message={validationErrors['password']} />
                </div>
                <div className="custom-control custom-checkbox mb-5">
                  <input type="checkbox" className="custom-control-input" id="terms" name="terms" required />
                  <label className="custom-control-label" htmlFor="terms">
                    I have read and agree to the{' '}
                    <a href="https://airportcars.com/terms-and-conditions" target="_blank" rel="noreferrer">
                      T&Cs
                    </a>
                    <br />
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
                    <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                  </label>
                </div>
                <button type="submit" className="btn btn-secondary btn-block border-0 btn-lg">
                  {isLoading ? <div className="spinner-border text-light mr-2" role="status"></div> : null}
                  <span>Complete</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

export default Register
